import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { toSafeInt } from "widgets/utils";

import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { mapSequenceTypeToEntityType } from "@/app-utils/route.ts";
import { type ISequenceState, shouldLoadSequence } from "@/redux/modules/sequence.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import SequenceSummaryView from "./SequenceSummaryView.js";

/**
 * The TranscriptPage smart component is called from the route corresponding to transcript/:transcriptId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. transcript/:transcriptId/summary)
 * The TranscriptPage will take care of filling up the basic transcript data and the nested child pages may request additional
 * data that is needed to show the nested page (such as transcript and transcript child collections for the summary page)
 */
const SequenceDetailPage: FC = () => {
  const { sequenceId } = useParams<"sequenceId">();
  const { sequence, isLoading } = useSelector<IAppState, ISequenceState>((state) => state.sequence);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadSequence, sequenceId);

  useEffect(() => {
    void load();
  }, [load]);

  const navItems: IRouteNavItemProps[] = [
    {
      label: "Summary",
      route: "summary",
      pageComponent: (
        <SequenceSummaryView
          sequenceId={toSafeInt(sequenceId)}
          entityType={mapSequenceTypeToEntityType(sequence?.sequenceGroup.name)}
        />
      ),
      default: true,
    },
  ];

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.SEQUENCE} // should we pass in the more specific entity type here, like we do to the page component above?
      entityId={toSafeInt(sequenceId)}
      getDisplayName={() => sequence?.accession}
      sidebarNavItems={navItems}
      isLoading={isLoading}
    />
  );
};

export default SequenceDetailPage;
